import { ReactNode, Suspense, forwardRef, lazy } from "react";
import { twMerge } from "tailwind-merge";

import { LoadingDotsIcon } from "mds/icons";

const Markdown = lazy(() => import("mds/components/Markdown"));

export type ChatBubbleProps = {
  className?: string;
  children?: ReactNode;
  header?: ReactNode;
  isSelf?: boolean; // Optional prop to indicate if the message is from the current user
  parseMarkdown?: boolean;
};

export const ChatBubble = forwardRef<HTMLDivElement, ChatBubbleProps>(function ChatBubbleInternal(
  { className, header, children, isSelf = false, parseMarkdown = false },
  ref,
) {
  // We markdown-transform relevant child elements
  let processedChildren = children || "";

  const childrenList = Array.isArray(children) ? children : [children];
  if (parseMarkdown) {
    const stringChildren = childrenList
      .filter((child) => typeof child === "string")
      // The AI response will sometimes insert literal `\n` to indicate newlines,
      // which we'll resolve here.
      .map((s) => s.replace("\\n", "\n"));
    const nonStringChildren = childrenList.filter((child) => typeof child !== "string");
    processedChildren = (
      <Suspense fallback={<LoadingDotsIcon />}>
        {stringChildren.map((child, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Markdown key={index} markdown={child} />
        ))}
        {nonStringChildren}
      </Suspense>
    );
  }

  return (
    <div
      className={twMerge(
        "flex min-w-0 max-w-full flex-col rounded-xl p-4",
        isSelf
          ? "ml-6 self-end rounded-tr-none bg-blue-tint-95"
          : "mr-6 self-start rounded-tl-none bg-black-tint-97",
        className,
      )}
      ref={ref}
    >
      {header}
      <div className="min-w-0 overflow-y-hidden whitespace-pre-wrap break-words text-sm font-normal text-black-tint-20">
        {processedChildren}
      </div>
    </div>
  );
});
